/**
 * Cancel a session: reason (+ comment when required) then unmatch participants.
 */
import * as React from 'react';
import { XCircle } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { Select } from '@/components/forms/Select';
import { Textarea } from '@/components/forms/Textarea';
import { CANCELLATION_REASON_SELECT_OPTIONS } from '@/components/sessions/session-configs';
import type { CancellationReasonCode } from '@/types/remote/session.types';
import {
  buildCancelPayload,
  cancellationRequiresComment,
} from '@/components/sessions/remote/session-form-utils';
import { useFormValidation } from '@/hooks/useFormValidation';
import { getErrorMessage } from '@/utils/errors-utils';
import { cancelSessionValidationSchema } from '@/utils/validation-schemas-utils';

/** Props for {@link CancelSessionModal}. */
interface CancelSessionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: {
    reasonCode: CancellationReasonCode;
    reasonDetail?: string;
  }) => Promise<void>;
  sessionTitle?: string;
  isSubmitting?: boolean;
}

/** Cancel a session: reason (+ comment when required) then unmatch participants. */
export const CancelSessionModal: React.FC<Readonly<CancelSessionModalProps>> = ({
  open,
  onClose,
  onConfirm,
  sessionTitle,
  isSubmitting = false,
}) => {
  const [reasonCode, setReasonCode] = React.useState<CancellationReasonCode | ''>('');
  const [reasonDetail, setReasonDetail] = React.useState('');
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const validation = useFormValidation(cancelSessionValidationSchema);
  const requiresComment = cancellationRequiresComment(reasonCode);

  React.useEffect(() => {
    if (!open) return;
    setReasonCode('');
    setReasonDetail('');
    setSubmitError(null);
    validation.resetValidation();
  }, [open, validation.resetValidation]);

  function handleReasonCodeChange(value: string): void {
    const nextCode = value as CancellationReasonCode;
    const next = { reasonCode: nextCode, reasonDetail };
    setReasonCode(nextCode);
    validation.validateField('reasonCode', nextCode, next);
    validation.markAsTouched('reasonCode');
  }

  function handleReasonDetailChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    const nextDetail = event.target.value;
    const next = { reasonCode, reasonDetail: nextDetail };
    setReasonDetail(nextDetail);
    validation.validateField('reasonDetail', nextDetail, next);
    validation.markAsTouched('reasonDetail');
  }

  async function handleConfirm(): Promise<void> {
    if (!reasonCode || !validation.validateForm({ reasonCode, reasonDetail })) return;

    try {
      await onConfirm(buildCancelPayload(reasonCode, reasonDetail));
      onClose();
    } catch (err) {
      setSubmitError(getErrorMessage(err));
    }
  }

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size="md"
      header={{
        preset: 'danger',
        iconSize: 'md',
        title: 'Annuler la séance',
        subtitle: sessionTitle || "Confirmer l'annulation de la séance",
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
    >
      <div className="space-y-4">
        <Select
          label="Motif d'annulation"
          required
          value={reasonCode}
          onChange={handleReasonCodeChange}
          disabled={isSubmitting}
          placeholder="-- Sélectionnez un motif --"
          options={CANCELLATION_REASON_SELECT_OPTIONS}
          state={validation.getFieldState('reasonCode')}
          error={validation.getFieldError('reasonCode')}
        />

        {requiresComment ? (
          <Textarea
            id="cancel-comment"
            label="Commentaire"
            required
            value={reasonDetail}
            onChange={handleReasonDetailChange}
            placeholder="Précisez le motif d'annulation..."
            rows={4}
            disabled={isSubmitting}
            maxLength={1000}
            showCharCount
            error={validation.getFieldError('reasonDetail')}
          />
        ) : null}

        {submitError ? (
          <div
            className="rounded-lg p-3 text-sm border"
            style={{
              background: 'color-mix(in srgb, rgb(var(--status-error)) 10%, transparent)',
              borderColor: 'rgb(var(--status-error))',
              color: 'rgb(var(--status-error))',
            }}
          >
            {submitError}
          </div>
        ) : null}

        <div
          className="rounded-lg p-3 text-sm border"
          style={{
            background: 'color-mix(in srgb, rgb(var(--status-warning)) 10%, transparent)',
            borderColor: 'rgb(var(--status-warning))',
            color: 'var(--color-1-nuance-1)',
          }}
        >
          <p className="font-medium mb-1">Attention</p>
          <p style={{ color: 'var(--muted-foreground)' }}>
            L'annulation d'une séance est définitive et dématchera tous les participants. Cette
            action ne peut pas être annulée.
          </p>
        </div>

        <div className="flex gap-3 justify-end pt-2">
          <Button preset="modal-cancel" onClick={onClose} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button
            variant="danger"
            icon={XCircle}
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={!validation.isFormValid() || isSubmitting}
          >
            Confirmer
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};
